/**
 * Save Version Modal (PRD-05c)
 *
 * Persists the Fixed Routes drawer's edits as a reusable **version** of the selected hosted
 * network — only the changed/added/removed routes (a delta), stored in the network store and
 * surfaced under Transit Networks → Versions. This is the in-app edit loop; it is distinct
 * from **Export → BODS** (which downloads a TransXChange file). No file round-trip.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, AlertTitle, Box, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { Icon } from '@liftango/ui-icons';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import { useNetworkBrowse } from '../../context/NetworkBrowseContext';
import { buildNetworkExportOverlay } from '../../utils/fixedRoutes/networkExportOverlay';
import { saveScenario } from '../../services/network/networkApi';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { formatDateAsWallTime } from '../../utils/dateTimeUtils';
import type { ServiceDaysMask } from '../../types/fixedRoutes';

interface SaveVersionModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save (e.g. to refresh the versions list / open the picker). */
  onSaved?: (name: string) => void;
}

const WEEKDAY_MASK: ServiceDaysMask = {
  monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
  saturday: false, sunday: false,
};

const plusOneYear = (isoDate: string): string => {
  const [y, m, d] = isoDate.split('-');
  return `${String(Number(y) + 1).padStart(4, '0')}-${m}-${d}`;
};

export function SaveVersionModal({ open, onClose, onSaved }: SaveVersionModalProps) {
  const { t } = useTranslation('fixedRoutes');
  const { stops, routes, trafficMultiplier } = useFixedRoutes();
  const { feedId, version } = useNetworkBrowse();
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London', []);

  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The edit delta (forks + new routes), with sensible default metadata — only the delta is stored.
  const overlayResult = useMemo(() => {
    if (!feedId) return null;
    return buildNetworkExportOverlay({ stops, routes, trafficMultiplier }, feedId, {
      defaultServiceDays: WEEKDAY_MASK,
      defaultServiceDateRange: { startDate: DEFAULT_SIMULATION_DATE, endDate: plusOneYear(DEFAULT_SIMULATION_DATE) },
      meta: {
        feedName: feedId, agencyName: feedId, agencyUrl: 'https://liftango.com',
        agencyTimezone: timezone, agencyLang: 'en', feedVersion: formatDateAsWallTime(new Date()).slice(0, 19),
      },
    });
  }, [feedId, stops, routes, trafficMultiplier, timezone]);

  const editedCount = overlayResult?.editedCount ?? 0;
  const newCount = overlayResult?.newCount ?? 0;
  const hasEdits = editedCount + newCount > 0;
  const canSave = !!feedId && hasEdits && !busy;

  const handleSave = async () => {
    if (!feedId || !overlayResult) return;
    setBusy(true);
    setError(null);
    try {
      const finalName = name.trim() || t('saveVersion.defaultName', {
        defaultValue: 'Edited {{date}}', date: formatDateAsWallTime(new Date()).slice(0, 10),
      });
      await saveScenario(feedId, { name: finalName, overlay: overlayResult.overlay, baseVersion: version ?? undefined });
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.network_version_saved, {
        feed_id: feedId, edited_count: editedCount, new_count: newCount, timestamp: new Date().toISOString(),
      });
      setName('');
      onSaved?.(finalName);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('saveVersion.error', 'Could not save the version.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon name="circle-plus" size={20} />
          {t('saveVersion.title', 'Save as a version')}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('saveVersion.description', 'Save your edits as a reusable version of this network. Only the changed routes are stored; it appears under Transit Networks → Versions.')}
          </Typography>

          <Alert severity={hasEdits ? 'info' : 'warning'} icon={<Icon name={hasEdits ? 'circle-info' : 'triangle-exclamation'} size={20} />}>
            {hasEdits
              ? t('saveVersion.summary', { defaultValue: '{{edited}} edited · {{added}} new route(s) will be saved.', edited: editedCount, added: newCount })
              : t('saveVersion.noEdits', 'No edits yet — fork or draw a route first, then save a version.')}
          </Alert>

          <TextField
            label={t('saveVersion.name', 'Version name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            placeholder={t('saveVersion.namePlaceholder', 'e.g. Peak boost')}
          />

          {error && (
            <Alert severity="error" icon={<Icon name="triangle-exclamation" size={20} />}>
              <AlertTitle>{t('saveVersion.errorTitle', 'Save failed')}</AlertTitle>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel', 'Cancel')}</Button>
        <Button
          variant="contained"
          startIcon={busy ? <Icon name="spinner" size={20} /> : <Icon name="circle-plus" size={20} />}
          onClick={handleSave}
          disabled={!canSave}
        >
          {t('saveVersion.save', 'Save version')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
